import { useEffect, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { toast } from "sonner";
import { api, apiError } from "@/lib/api";
import { Logo } from "@/components/Logo";
import { Footer } from "@/components/Footer";
import { useSeo } from "@/hooks/useSeo";
import {
  IconCopy, IconCheck, IconAmbShare, IconChat, IconUsers, IconUserCheck,
  IconClockFast, IconAmbSeal, IconTimes, IconWallet, IconTrend,
} from "@/components/BrandGraphics";

const euro = (n) => `${(Number(n) || 0).toLocaleString("fr-FR")} €`;

const COMM_LABELS = {
  en_attente: { label: "En attente", cls: "bg-neutral-100 text-neutral-600" },
  validee: { label: "Validée", cls: "bg-blue-50 text-blue-700" },
  a_payer: { label: "À payer", cls: "bg-amber-50 text-amber-700" },
  payee: { label: "Payée", cls: "bg-emerald-50 text-emerald-700" },
  annulee: { label: "Annulée", cls: "bg-red-50 text-red-700" },
};

function StatCard({ icon: Icon, label, value, accent, testId }) {
  return (
    <div className="rounded-2xl border border-neutral-200 bg-white p-fib-4" data-testid={testId}>
      <div className={`flex h-9 w-9 items-center justify-center rounded-full ${accent || "bg-brand/10 text-brand"}`}>
        <Icon className="h-4 w-4" />
      </div>
      <p className="mt-fib-3 text-gr-xl font-black text-neutral-900">{value}</p>
      <p className="text-gr-xs text-neutral-500">{label}</p>
    </div>
  );
}

export default function AmbassadeurDashboard() {
  const location = useLocation();
  useSeo({ title: "Tableau de bord ambassadeur — Plan Permis", description: "Suivez vos candidats et vos commissions." });

  const [creds, setCreds] = useState({ email: "", code: "" });
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [copied, setCopied] = useState(false);

  const load = async (email, code) => {
    setLoading(true);
    try {
      const { data: d } = await api.post("/ambassadors/dashboard", { email, code });
      setData(d);
    } catch (err) {
      toast.error(apiError(err.response?.data?.detail) || err.message);
      setData(null);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const st = location.state;
    if (st?.email && st?.code) {
      setCreds({ email: st.email, code: st.code });
      load(st.email, st.code);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const submit = (e) => {
    e.preventDefault();
    if (!creds.email || !creds.code) {
      toast.error("Renseignez votre email et votre code ambassadeur.");
      return;
    }
    load(creds.email.trim(), creds.code.trim().toUpperCase());
  };

  const copyCode = async () => {
    try {
      await navigator.clipboard.writeText(data.code);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      toast.error("Impossible de copier.");
    }
  };

  const shareText = data
    ? `Tu cherches une auto-école avec une date d'examen rapide ? Passe par Plan Permis et indique mon code ${data.code} lors de ta demande : ${window.location.origin}/`
    : "";
  const shareUrl = `${window.location.origin}/`;

  const shareNative = async () => {
    if (navigator.share) {
      try {
        await navigator.share({ title: "Plan Permis", text: shareText, url: shareUrl });
      } catch {
        /* annulé */
      }
    } else {
      await navigator.clipboard.writeText(shareText);
      toast.success("Message copié — collez-le où vous voulez !");
    }
  };

  const inputCls = "w-full rounded-lg border border-neutral-300 bg-white px-4 py-3 outline-none focus:border-brand transition-colors";

  if (!data) {
    return (
      <div className="flex min-h-screen flex-col bg-[#FAFAFA]">
        <header className="px-6 py-5 sm:px-12">
          <Link to="/"><Logo className="h-12" /></Link>
        </header>
        <div className="flex flex-1 items-center justify-center px-6 py-10">
          <form onSubmit={submit} className="w-full max-w-md rounded-3xl border border-neutral-200 bg-white p-8 shadow-lg" data-testid="ambassador-login-form">
            <h1 className="text-2xl font-extrabold tracking-tight text-neutral-900">Mon tableau de bord</h1>
            <p className="mt-2 text-sm text-neutral-500">Connectez-vous avec votre email et votre code ambassadeur.</p>
            <input data-testid="dash-email-input" type="email" className={`${inputCls} mt-6`} placeholder="Email" value={creds.email} onChange={(e) => setCreds((c) => ({ ...c, email: e.target.value }))} />
            <input data-testid="dash-code-input" className={`${inputCls} mt-4 uppercase`} placeholder="Code ambassadeur (PP-XXXX-01)" value={creds.code} onChange={(e) => setCreds((c) => ({ ...c, code: e.target.value }))} />
            <button type="submit" disabled={loading} data-testid="dash-login-btn" className="mt-6 w-full rounded-full bg-brand px-8 py-4 text-base font-bold text-white transition-colors hover:bg-brand-hover disabled:opacity-60">
              {loading ? "Connexion…" : "Voir mon tableau de bord"}
            </button>
            <p className="mt-4 text-center text-sm text-neutral-500">
              Pas encore ambassadeur ?{" "}
              <Link to="/ambassadeur" className="font-semibold text-brand hover:underline">Inscrivez-vous</Link>
            </p>
          </form>
        </div>
        <Footer />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#FAFAFA]">
      <header className="flex items-center justify-between px-6 py-5 sm:px-12">
        <Link to="/"><Logo className="h-12" /></Link>
        <button onClick={() => setData(null)} data-testid="dash-logout-btn" className="text-sm font-semibold text-neutral-500 hover:text-red-600">
          Se déconnecter
        </button>
      </header>

      <div className="mx-auto max-w-6xl px-6 pb-20 sm:px-12">
        <h1 className="text-3xl font-black tracking-tight text-neutral-900" data-testid="dash-hello">
          Bonjour {data.first_name} 👋
        </h1>
        <div className="mt-2 flex flex-wrap items-center gap-2" data-testid="dash-type-info">
          <span className={`rounded-full px-3 py-1 text-xs font-bold ${data.type === "professionnel" ? "bg-ink/10 text-ink" : "bg-brand/10 text-brand"}`}>
            {data.type === "professionnel" ? "Parrain professionnel" : "Parrain particulier"} · {euro(data.rate)} / filleul
          </span>
          {data.monthly_cap != null ? (
            <span className="rounded-full bg-neutral-100 px-3 py-1 text-xs font-semibold text-neutral-600" data-testid="dash-cap-info">
              Plafond mensuel : {data.remaining_this_month} / {data.monthly_cap} filleuls rémunérés restants ce mois
            </span>
          ) : (
            <span className="rounded-full bg-neutral-100 px-3 py-1 text-xs font-semibold text-neutral-600" data-testid="dash-cap-info">
              Aucun plafond mensuel
            </span>
          )}
        </div>

        {/* Code + partage */}
        <div className="mt-6 grid gap-4 lg:grid-cols-3">
          <div className="rounded-3xl bg-gradient-to-br from-brand to-brand-active p-6 text-white lg:col-span-1">
            <p className="text-xs font-bold uppercase tracking-wider text-white/70">Mon code ambassadeur</p>
            <p className="mt-2 text-3xl font-black tracking-wider" data-testid="dash-code">{data.code}</p>
            <button onClick={copyCode} data-testid="dash-copy-code-btn" className="mt-4 inline-flex items-center gap-2 rounded-full bg-white px-4 py-2 text-sm font-bold text-brand transition-transform hover:scale-[1.03]">
              {copied ? <IconCheck className="h-4 w-4" /> : <IconCopy className="h-4 w-4" />}
              {copied ? "Copié !" : "Copier mon code"}
            </button>
          </div>
          <div className="rounded-3xl border border-neutral-200 bg-white p-6 lg:col-span-2">
            <p className="text-sm font-bold text-neutral-800">Partager Plan Permis</p>
            <p className="mt-1 text-xs text-neutral-500">Diffusez votre code : chaque inscription validée vous rapporte.</p>
            <div className="mt-4 flex flex-wrap gap-3">
              <a
                href={`https://wa.me/?text=${encodeURIComponent(shareText)}`}
                target="_blank"
                rel="noreferrer"
                data-testid="share-whatsapp-btn"
                className="inline-flex items-center gap-2 rounded-full bg-[#25D366] px-5 py-2.5 text-sm font-bold text-white transition-transform hover:scale-[1.03]"
              >
                <IconChat className="h-4 w-4" /> WhatsApp
              </a>
              <a
                href={`sms:?&body=${encodeURIComponent(shareText)}`}
                data-testid="share-sms-btn"
                className="inline-flex items-center gap-2 rounded-full bg-neutral-800 px-5 py-2.5 text-sm font-bold text-white transition-transform hover:scale-[1.03]"
              >
                <IconChat className="h-4 w-4" /> SMS
              </a>
              <button
                onClick={shareNative}
                data-testid="share-native-btn"
                className="inline-flex items-center gap-2 rounded-full border border-neutral-300 px-5 py-2.5 text-sm font-bold text-neutral-700 transition-colors hover:border-brand hover:text-brand"
              >
                <IconAmbShare className="h-4 w-4" /> Réseaux sociaux
              </button>
            </div>
          </div>
        </div>

        {/* Stats candidats */}
        <h2 className="mt-10 text-lg font-bold text-neutral-800">Mes candidats</h2>
        <div className="mt-4 grid gap-4 sm:grid-cols-2 lg:grid-cols-5">
          <StatCard icon={IconUsers} label="Candidats apportés" value={data.candidates_total} testId="stat-candidates" />
          <StatCard icon={IconUserCheck} label="Contactés" value={data.contacted} testId="stat-contacted" />
          <StatCard icon={IconClockFast} label="Inscriptions en attente" value={data.pending_inscriptions} accent="bg-amber-100 text-amber-700" testId="stat-pending" />
          <StatCard icon={IconAmbSeal} label="Inscriptions validées" value={data.validated_inscriptions} accent="bg-emerald-100 text-emerald-700" testId="stat-validated" />
          <StatCard icon={IconTimes} label="Annulées" value={data.cancelled_inscriptions} accent="bg-red-100 text-red-700" testId="stat-cancelled" />
        </div>

        {/* Stats commissions */}
        <h2 className="mt-10 text-lg font-bold text-neutral-800">Mes commissions</h2>
        <div className="mt-4 grid gap-4 sm:grid-cols-2 lg:grid-cols-5">
          <StatCard icon={IconClockFast} label="En attente (estimation)" value={euro(data.commission_pending)} accent="bg-neutral-100 text-neutral-600" testId="stat-comm-pending" />
          <StatCard icon={IconWallet} label="Validée" value={euro(data.commission_validated)} accent="bg-blue-100 text-blue-700" testId="stat-comm-validated" />
          <StatCard icon={IconAmbSeal} label="Déjà payée" value={euro(data.commission_paid)} accent="bg-emerald-100 text-emerald-700" testId="stat-comm-paid" />
          <StatCard icon={IconTrend} label="Gagné ce mois" value={euro(data.earned_this_month)} testId="stat-earned-month" />
          <StatCard icon={IconTrend} label="Gagné depuis le début" value={euro(data.earned_total)} accent="bg-brand text-white" testId="stat-earned-total" />
        </div>

        {/* Historique commissions */}
        <h2 className="mt-10 text-lg font-bold text-neutral-800">Détail des commissions</h2>
        <div className="mt-4 overflow-x-auto rounded-2xl border border-neutral-200 bg-white">
          <table className="w-full text-sm" data-testid="dash-commissions-table">
            <thead className="border-b border-neutral-200 bg-neutral-50 text-left text-xs uppercase text-neutral-500">
              <tr>
                <th className="px-4 py-3">Mois</th>
                <th className="px-4 py-3">Candidat</th>
                <th className="px-4 py-3">Auto-école</th>
                <th className="px-4 py-3">Montant</th>
                <th className="px-4 py-3">Statut</th>
              </tr>
            </thead>
            <tbody>
              {data.commissions.length === 0 ? (
                <tr><td colSpan={5} className="px-4 py-8 text-center text-neutral-400">Aucune commission pour le moment.</td></tr>
              ) : (
                data.commissions.map((c) => {
                  const s = COMM_LABELS[c.status] || COMM_LABELS.en_attente;
                  return (
                    <tr key={c.id} className="border-b border-neutral-100 last:border-0">
                      <td className="px-4 py-3">{c.month}</td>
                      <td className="px-4 py-3 font-medium text-neutral-800">{c.candidate_name}</td>
                      <td className="px-4 py-3 text-neutral-600">{c.school_name || "—"}</td>
                      <td className="px-4 py-3 font-bold text-neutral-900">{euro(c.amount)}</td>
                      <td className="px-4 py-3"><span className={`rounded-full px-2.5 py-1 text-xs font-semibold ${s.cls}`}>{s.label}</span></td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
      </div>
      <Footer />
    </div>
  );
}
